import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { AlertCircle, Calendar, MapPin } from 'lucide-react';
import { Event } from '../types/event.types';
import { FamilyMember } from '../types/family.types';
import {
  getAgeRequirementBadgeVariant,
  getBasicAgeRequirementDescription,
  getIncompatibilityWarning,
  validateEventAttendees,
} from '../utils/eventValidation';
import { EventTime } from './EventTime';
import { EventStatus } from '@/types/enums.ts';

interface EventCardProps {
  event: Event;
  selectedMembers: string[];
  memberDetails: FamilyMember[];
  isSelectingForFamily: boolean;
  isCheckedIn: boolean;
  onEventSelect: (eventId: string) => void;
  onCheckIn: (eventId: string) => void;
  showActionButton?: boolean;
}

interface EventButtonConfig {
  text: string;
  variant: string;
  disabled: boolean;
}

const isEventRunningToday = (startDateTime: string): boolean => {
  if (!startDateTime) return false;

  const now = new Date();
  const todayUtcStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const todayUtcEnd = todayUtcStart + 24 * 60 * 60 * 1000;

  const eventDate = new Date(startDateTime);
  const eventTs = eventDate.getTime();

  if (isNaN(eventTs)) return false;

  return eventTs >= todayUtcStart && eventTs < todayUtcEnd;
};

const shouldShowSelectMembersButton = (event: Event): boolean => {
  /*
   Hide for drop-in events that are not running today
   Hide for registered events that are not in "Registration opened" or in "Opened" statuses
   */
  return event.dropIn
    ? isEventRunningToday(event.startDateTime)
    : event.status === EventStatus.REG_OPENED || event.status === EventStatus.OPENED;
};

const getEventButtonConfig = (
  isSelectingForFamily: boolean,
  isCheckedIn: boolean,
  compatibleCount: number,
  totalCount: number,
): EventButtonConfig & { countValues?: Record<string, unknown> } => {
  if (!isSelectingForFamily) {
    return { text: 'buttons.selectMembers', variant: 'btn-event', disabled: false };
  }

  if (isCheckedIn) {
    return {
      text: 'buttons.checkedIn',
      variant: 'bg-[var(--color-success-500)] hover:bg-[var(--color-success-500)] cursor-not-allowed',
      disabled: true,
    };
  }

  if (compatibleCount === 0) {
    return {
      text: 'buttons.cannotAttend',
      variant: 'bg-[var(--color-neutral-400)] hover:bg-[var(--color-neutral-400)] cursor-not-allowed',
      disabled: true,
    };
  }

  if (compatibleCount < totalCount) {
    return {
      text: 'buttons.checkInPartial',
      variant: 'btn-event',
      disabled: false,
      countValues: { compatible: compatibleCount, total: totalCount },
    };
  }

  return { text: 'buttons.checkIn', variant: 'btn-event', disabled: false };
};

export const EventCard = ({
  event,
  memberDetails,
  isSelectingForFamily,
  isCheckedIn,
  onEventSelect,
  onCheckIn,
  showActionButton = true,
}: EventCardProps) => {
  const { t, i18n } = useTranslation('common');
  const [isNavigating, setIsNavigating] = useState(false);
  const validation = validateEventAttendees(event, memberDetails);
  const ageRequirement = getBasicAgeRequirementDescription(event);
  const badgeVariant = getAgeRequirementBadgeVariant(event);
  const incompatibilityWarning = getIncompatibilityWarning(event, memberDetails);

  const buttonConfig = getEventButtonConfig(
    isSelectingForFamily,
    isCheckedIn,
    validation.compatibleMembers.length,
    memberDetails.length,
  );

  const showSelectMembersButton = shouldShowSelectMembersButton(event);

  const handleClick = () => {
    if (isNavigating) return; // Prevent multiple clicks

    setIsNavigating(true);

    if (!isSelectingForFamily) {
      onEventSelect(event.id);
    } else {
      onCheckIn(event.id);
    }

    // Reset navigation state after a short delay to allow for navigation
    setTimeout(() => setIsNavigating(false), 1000);
  };

  const cardClassName = `card-interactive transition-all duration-200 hover:shadow-lg hover:scale-[1.02] w-full max-w-md ${
    isCheckedIn
      ? 'ring-2 ring-[var(--color-success-500)] bg-[var(--color-success-50)]'
      : !validation.canCheckIn
        ? 'ring-2 ring-[var(--color-error-100)] bg-[var(--color-error-100)]'
        : 'hover:shadow-md lg:hover:shadow-xl'
  }`;

  return (
    <Card className={cardClassName}>
      <CardContent className="p-3 md:p-4">
        <div className="flex flex-col gap-3 items-center text-center">
          <div className="flex-1 w-full">
            <div className="flex flex-col gap-2 items-center">
              <div className="flex items-center justify-center gap-2 text-xs text-[var(--color-neutral-400)] mb-2">
                <Calendar className="h-3 w-3" />
                <span className="font-medium">
                  {new Date(event.startDateTime).toLocaleDateString(i18n.language, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </div>

              <h4 className="text-base md:text-base lg:text-lg font-medium text-[var(--color-neutral-900)] break-words">
                {event.name}
              </h4>
              <div className="mt-1 md:mt-2 space-y-1 flex flex-col items-center">
                <div className="flex items-center justify-center text-xs md:text-sm text-[var(--color-neutral-400)]">
                  <MapPin className="h-3 w-3 mr-1 shrink-0" />
                  <span>{event.location || t('events.noLocation')}</span>
                </div>
                <EventTime startDateTime={event.startDateTime} endDateTime={event.endDateTime} />
              </div>

              <div className="flex flex-wrap items-center justify-center gap-1.5 mt-2">
                {event.dropIn ? (
                  <Badge className="badge-event text-xs md:text-sm">{t('events.dropInEvent')}</Badge>
                ) : (
                  <Badge variant="warning" className="text-xs md:text-sm">
                    {t('events.registrationRequired')}
                  </Badge>
                )}

                {ageRequirement && (
                  <Badge variant={badgeVariant} className="text-xs md:text-sm">
                    {t(ageRequirement)}
                  </Badge>
                )}
                {event.ageRequirements?.minAge && event.ageRequirements?.maxAge && (
                  <p className="flex items-center text-xs md:text-sm text-[var(--color-neutral-400)]">
                    {t('events.ageRange', { min: event.ageRequirements.minAge, max: event.ageRequirements.maxAge })}
                  </p>
                )}
                {event.ageRequirements?.requiresGuardian && (
                  <p className="flex items-center text-xs md:text-sm text-[var(--color-neutral-400)]">
                    {t('events.guardianRequired')}
                  </p>
                )}
              </div>
            </div>

            <div className="hidden md:block mt-2 space-y-2 text-center">
              {event.description && (
                <p className="text-xs text-(--color-neutral-400) line-clamp-2">{event.description}</p>
              )}
            </div>

            {isSelectingForFamily && incompatibilityWarning && (
              <Alert className="mt-3 bg-(--color-warning-100) border-(--color-warning-500) text-center">
                <AlertCircle className="h-4 w-4 text-(--color-warning-700) mx-auto" />
                <AlertDescription className="text-(--color-warning-900)">{incompatibilityWarning}</AlertDescription>
              </Alert>
            )}
          </div>

          {((!isSelectingForFamily && showSelectMembersButton) || isSelectingForFamily) && showActionButton ? (
            <div className="mt-3">
              <Button
                variant="default"
                onClick={handleClick}
                disabled={buttonConfig.disabled || isNavigating}
                className={`w-full ${buttonConfig.variant}`}
              >
                {isNavigating
                  ? (t('labels.loading') as string)
                  : (t(buttonConfig.text, buttonConfig.countValues) as string)}
              </Button>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};
